import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Button, Grid, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Avatar, Divider, Alert, AlertTitle, LinearProgress, Skeleton, TablePagination, FormControl, InputLabel, Select, MenuItem, Tooltip, InputAdornment } from '@mui/material';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LanguageIcon from '@mui/icons-material/Language';
import ReplayIcon from '@mui/icons-material/Replay';
import { OtaService } from '../../api/services/otaService';
import { RoomService } from '../../api/services/roomService';
import { RateService } from '../../api/services/rateService';
import { toast } from '../../utils/toast';
import OTAChannelOnboardingModal from '../../components/OTAChannelOnboardingModal';
import OTAChannelDetailsDrawer from '../../components/OTAChannelDetailsDrawer';
import { useCurrency, formatCurrency } from '../../utils/currency';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardResetButtonSx } from '../../components/common/tableStyles';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function ChannelManagement() {
    const { currency } = useCurrency();
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth);
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    console.log(hotelStatus,"hotelStatus",tenant?.status)

    const [tabValue, setTabValue] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [onboardingOpen, setOnboardingOpen] = useState(false);
    
    // --- Channel Details Drawer State ---
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedChannelId, setSelectedChannelId] = useState(null);

    // --- Booking Log State ---
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logPage, setLogPage] = useState(0);
    const [logRowsPerPage, setLogRowsPerPage] = useState(20);
    const [logTotal, setLogTotal] = useState(0);
    const [logChannel, setLogChannel] = useState('');
    const [logStatus, setLogStatus] = useState('All');
    const [debouncedLogChannel, setDebouncedLogChannel] = useState('');

    // --- Retry Booking State ---
    const [retryingLogs, setRetryingLogs] = useState({});

    // --- Performance State ---
    const [performanceData, setPerformanceData] = useState([]);
    const [performanceLoading, setPerformanceLoading] = useState(false);

    // --- Dynamic Lookups ---
    const [roomTypes, setRoomTypes] = useState([]);
    const [ratePlans, setRatePlans] = useState([]);

    // Debounce log channel search by 400ms
    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedLogChannel(logChannel);
        }, 400);
        return () => clearTimeout(handler);
    }, [logChannel]);

    // Fetch Logs
    const fetchLogs = async () => {
        setLogsLoading(true);
        try {
            const response = await OtaService.getBookingsLog({
                channel: debouncedLogChannel,
                status: logStatus,
                page: logPage,
                limit: logRowsPerPage
            });
            const logsList = Array.isArray(response.data) ? response.data : (Array.isArray(response.logs) ? response.logs : (Array.isArray(response) ? response : []));
            setLogs(logsList);
            setLogTotal(extractTotalCount(response, logsList));
        } catch (error) {
            console.error("Failed to fetch logs:", error);
        } finally {
            setLogsLoading(false);
        }
    };

    React.useEffect(() => {
        if (tabValue === 1) { // Tab 1 is Booking Import Log
            fetchLogs();
        }
    }, [debouncedLogChannel, logStatus, logPage, logRowsPerPage, tabValue]);

    const handleRetryImport = async (logId) => {
        setRetryingLogs(prev => ({ ...prev, [logId]: true }));
        try {
            await OtaService.retryBookingLog(logId);
            toast.success("Booking sync re-ingested and processed successfully");
            await fetchLogs();
        } catch (error) {
            console.error('Failed to retry booking import:', error);
        } finally {
            setRetryingLogs(prev => ({ ...prev, [logId]: false }));
        }
    };

    const fetchPerformanceData = async () => {
        setPerformanceLoading(true);
        try {
            const data = await OtaService.getPerformanceReport();
            setPerformanceData(data);
        } catch (error) {
            console.error('Failed to fetch performance report:', error);
        } finally {
            setPerformanceLoading(false);
        }
    };

    React.useEffect(() => {
        if (tabValue === 2) { // Tab 2 is Operational Analytics
            fetchPerformanceData();
        }
    }, [tabValue]);

    const fetchChannels = async () => {
        setLoading(true);
        try {
            // Flat connection aggregator. No pagination, sorting, or filtering params sent.
            const data = await OtaService.getConnectedChannels();
            setChannels(data);
        } catch (error) {
            // Let the global client network interceptor handle the error and toast
            console.error("Failed to fetch channels:", error);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchChannels();
        fetchLookups();
    }, []);

    const fetchLookups = async () => {
        try {
            const [roomsData, ratesData] = await Promise.all([
                RoomService.getRoomTypes(),
                RateService.getRatePlans()
            ]);
            setRoomTypes(Array.isArray(roomsData) ? roomsData : roomsData?.data || []);
            setRatePlans(Array.isArray(ratesData) ? ratesData : ratesData?.data || []);
        } catch (err) {
            console.error("Failed to fetch lookups", err);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSync = async () => {
        setIsSyncing(true);
        try {
            const connectedChannels = channels.filter(c => c.status === 'Connected' || c.status === 'Error');
            if (connectedChannels.length > 0) {
                await Promise.all(connectedChannels.map(c => OtaService.syncChannel(c.id)));
                toast.success("All channels synchronized successfully");
            } else {
                toast.info("No connected channels to sync");
            }
            await fetchChannels();
        } catch (error) {
            console.error("Failed to sync channels:", error);
        } finally {
            setIsSyncing(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Connected': return 'success';
            case 'Error': return 'error';
            case 'Disconnected': return 'default';
            default: return 'default';
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">OTA & Channel Management</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined" 
                        startIcon={<SyncIcon sx={{ animation: isSyncing ? 'spin 1s linear infinite' : 'none' }} />}
                        onClick={handleSync}
                        disabled={isSyncing}
                    >
                        {isSyncing ? 'Syncing...' : 'Sync All Channels'}
                    </Button>
                    <Tooltip title={['Suspended', 'Archived'].includes(hotelStatus) ? "Channel connections are locked for Suspended/Archived properties" : ""}>
                        <span>
                            <Button 
                                variant="contained" 
                                startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                                onClick={() => setOnboardingOpen(true)}
                                disabled={['Suspended', 'Archived'].includes(hotelStatus)}
                            >
                                Connect Channel
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

                <OTAChannelOnboardingModal 
                open={onboardingOpen}
                onClose={() => setOnboardingOpen(false)}
                onSuccess={() => fetchChannels()}
                roomTypes={roomTypes}
                ratePlans={ratePlans}
            />

            <OTAChannelDetailsDrawer 
                open={detailsOpen}
                channelId={selectedChannelId}
                onClose={() => {
                    setDetailsOpen(false);
                    setSelectedChannelId(null);
                }}
                onDisconnect={() => fetchChannels()}
            />

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>

            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold">{loading ? <Skeleton width={40} sx={{ mx: 'auto' }} /> : channels.filter(c => c.status === 'Connected').length}</Typography>
                        <Typography variant="caption" color="text.secondary">Active Channels</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">{loading ? <Skeleton width={40} sx={{ mx: 'auto' }} /> : channels.reduce((sum, c) => sum + (c.bookingsToday || 0), 0)}</Typography>
                        <Typography variant="caption" color="text.secondary">Bookings Today</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="error.main">{loading ? <Skeleton width={40} sx={{ mx: 'auto' }} /> : channels.filter(c => c.status === 'Error').length}</Typography>
                        <Typography variant="caption" color="text.secondary">Sync Errors</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold">{loading ? <Skeleton width={40} sx={{ mx: 'auto' }} /> : (channels.length > 0 ? ((channels.filter(c => c.status !== 'Error').length / channels.length) * 100).toFixed(1) + '%' : '100%')}</Typography>
                        <Typography variant="caption" color="text.secondary">Uptime Rate</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {!loading && channels.some(c => c.status === 'Error') && (
                <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
                    <AlertTitle>Channel Sync Errors</AlertTitle>
                    One or more channels have failed to sync recently. Please check the connection credentials.
                </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                    <Tab label="Connected Channels" />
                    <Tab label="Booking Import Log" />
                    <Tab label="Operational Analytics" />
                </Tabs>
            </Box>

            <Box>
                    {/* Channels List */}
                    <TabPanel value={tabValue} index={0}>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Channel</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Active Rate Plans</TableCell>
                                        <TableCell>Last Sync</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        Array.from(new Array(3)).map((_, i) => (
                                            <TableRow key={`skeleton-${i}`}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                        <Skeleton variant="circular" width={32} height={32} />
                                                        <Skeleton width={100} />
                                                    </Box>
                                                </TableCell>
                                                <TableCell><Skeleton width={80} /></TableCell>
                                                <TableCell><Skeleton width={40} /></TableCell>
                                                <TableCell><Skeleton width={120} /></TableCell>
                                                <TableCell align="right"><Skeleton width={150} sx={{ display: 'inline-block' }} /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : channels.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                                <Typography variant="h6" color="text.secondary">No OTA Channels Connected</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        channels.map((channel) => (
                                            <TableRow key={channel.id} hover>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar 
                                                            src={channel.logo} 
                                                            alt={channel.name}
                                                            sx={{ width: 32, height: 32, bgcolor: 'transparent', color: 'text.primary' }}
                                                            variant="rounded"
                                                        >
                                                            {!channel.logo && <LanguageIcon fontSize="small" />}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">{channel.name}</Typography>
                                                            {channel.error && (
                                                                <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                                                                    {channel.error}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={channel.status || 'Unknown'} 
                                                        size="small" 
                                                        color={getStatusColor(channel.status)} 
                                                        icon={channel.status === 'Connected' ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{channel.activeRatePlans || channel.bookingsToday || 0}</TableCell>
                                                <TableCell>
                                                    {(channel.lastSyncTime || channel.lastSync) ? new Date(channel.lastSyncTime || channel.lastSync).toLocaleTimeString() : 'Never'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <Tooltip title={['Suspended', 'Archived'].includes(hotelStatus) ? "Channel connections are locked for Suspended/Archived properties" : ""}>
                                                            <span>
                                                                <Button 
                                                                    variant="outlined" 
                                                                    size="small" 
                                                                    startIcon={<SettingsIcon />}
                                                                    onClick={() => {
                                                                        setSelectedChannelId(channel.id);
                                                                        setDetailsOpen(true);
                                                                    }}
                                                                    disabled={['Suspended', 'Archived'].includes(hotelStatus)}
                                                                >
                                                                    Config
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>



                    {/* Booking Log */}
                    <TabPanel value={tabValue} index={1}>
                        <Box sx={standardFilterContainerSx}>
                            <TextField
                                size="small"
                                placeholder="Search by channel..."
                                value={logChannel}
                                onChange={(e) => {
                                    setLogChannel(e.target.value);
                                    setLogPage(0);
                                }}
                                sx={standardSearchInputSx}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Select
                                        value={logStatus}
                                        displayEmpty
                                        onChange={(e) => {
                                            setLogStatus(e.target.value);
                                            setLogPage(0);
                                        }}
                                        sx={standardFilterSelectSx}
                                    >
                                        <MenuItem value="All">All Statuses</MenuItem>
                                        <MenuItem value="Imported">Imported</MenuItem>
                                        <MenuItem value="Failed">Failed</MenuItem>
                                        <MenuItem value="Duplicate">Duplicate</MenuItem>
                                    </Select>
                                </FormControl>
                                {(logChannel !== '' || logStatus !== 'All') && (
                                    <Button 
                                        variant="outlined" 
                                        startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} 
                                        sx={standardResetButtonSx}
                                        onClick={() => {
                                            setLogChannel('');
                                            setLogStatus('All');
                                            setLogPage(0);
                                        }}
                                    >
                                        Reset
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>OTA Reference</TableCell>
                                        <TableCell>Channel</TableCell>
                                        <TableCell>Execution Time</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Message</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logsLoading ? (
                                        Array.from(new Array(logRowsPerPage)).map((_, i) => (
                                            <TableRow key={i}>
                                                <TableCell><Skeleton /></TableCell>
                                                <TableCell><Skeleton /></TableCell>
                                                <TableCell><Skeleton /></TableCell>
                                                <TableCell><Skeleton /></TableCell>
                                                <TableCell><Skeleton /></TableCell>
                                                <TableCell align="right"><Skeleton width={60} sx={{ display: 'inline-block' }} /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : logs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">No booking logs match your criteria.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        logs.map((log) => (
                                            <TableRow key={log.id} hover>
                                                <TableCell><b>{log.otaReference || log.id}</b></TableCell>
                                                <TableCell>{log.channel}</TableCell>
                                                <TableCell>{log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={log.status} 
                                                        size="small" 
                                                        color={log.status === 'Imported' ? 'success' : log.status === 'Duplicate' ? 'warning' : 'error'} 
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ color: log.status !== 'Imported' ? 'error.main' : 'text.primary' }}>
                                                    {log.message || log.error || 'Success'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {log.status === 'Failed' ? (
                                                        <Button 
                                                            size="small" 
                                                            color="primary" 
                                                            variant="outlined"
                                                            startIcon={retryingLogs[log.id] ? <CircularProgress size={16} /> : <ReplayIcon />}
                                                            disabled={retryingLogs[log.id]}
                                                            onClick={() => handleRetryImport(log.id)}
                                                        >
                                                            {retryingLogs[log.id] ? 'Retrying...' : 'Retry Import'}
                                                        </Button>
                                                    ) : (
                                                        <Button size="small" disabled={true}>View Detail</Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={logs.length === 0 ? 0 : logTotal}
                                page={logPage}
                                onPageChange={(e, newPage) => setLogPage(newPage)}
                                rowsPerPage={logRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setLogRowsPerPage(parseInt(e.target.value, 10));
                                    setLogPage(0);
                                }}
                                rowsPerPageOptions={[20, 50, 100]}
                            />
                        </TableContainer>
                    </TabPanel>

                    {/* Operational Analytics */}
                    <TabPanel value={tabValue} index={2}>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Channel / Provider ID</TableCell>
                                        <TableCell align="right">Total Revenue</TableCell>
                                        <TableCell align="right">Total Bookings</TableCell>
                                        <TableCell align="center">Cancellation Rate</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {performanceLoading ? (
                                        Array.from(new Array(3)).map((_, i) => (
                                            <TableRow key={`perf-skel-${i}`}>
                                                <TableCell><Skeleton /></TableCell>
                                                <TableCell><Skeleton /></TableCell>
                                                <TableCell><Skeleton /></TableCell>
                                                <TableCell><Skeleton /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : performanceData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">No performance metrics available.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        performanceData.map((row, idx) => (
                                            <TableRow key={idx} hover>
                                                <TableCell><b>{row.channelName || row.providerId || 'Unknown'}</b></TableCell>
                                                <TableCell align="right">{formatCurrency(row.totalRevenue || 0, currency)}</TableCell>
                                                <TableCell align="right">{row.totalBookings || 0}</TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={`${row.cancellationRate || 0}%`}
                                                        color={(row.cancellationRate || 0) > 20 ? 'error' : (row.cancellationRate || 0) > 10 ? 'warning' : 'success'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>
                </Box>
        </Box>
    );
}
